// Linear-interpolation resampler with carry state so consecutive calls
// are seamless (the AudioWorklet feeds 128-sample blocks; the fractional
// read position and the previous block's last sample carry across calls).
// Float32 [-1, 1] in, Int16 out.

export class Resampler {
  private pos = 0; // fractional read position into the virtual stream
  private prev = 0; // last sample of the previous block
  private hasPrev = false;

  constructor(
    private inRate: number,
    private outRate: number,
  ) {}

  process(input: Float32Array): Int16Array {
    if (input.length === 0) return new Int16Array(0);
    const n = input.length;
    const ratio = this.inRate / this.outRate;
    // Virtual stream S = [prev, ...input]; S[0] is the previous block's
    // last sample, S[k] = input[k-1]. Starting the very first block at
    // position 1 makes output[0] === input[0] (zero-latency start).
    if (!this.hasPrev) this.pos = 1;
    const s = (idx: number): number =>
      idx === 0 ? (this.hasPrev ? this.prev : input[0]!) : input[idx - 1]!;
    const out: number[] = [];
    for (;;) {
      const i = Math.floor(this.pos);
      const frac = this.pos - i;
      const need = frac > 0 ? i + 1 : i;
      if (need > n) break;
      const v = frac > 0 ? s(i) + (s(i + 1) - s(i)) * frac : s(i);
      out.push(Math.max(-32768, Math.min(32767, Math.round(v * 32767))));
      this.pos += ratio;
    }
    this.pos -= n;
    this.prev = input[n - 1]!;
    this.hasPrev = true;
    return Int16Array.from(out);
  }
}
